import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Container from "layouts/container/Container";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import Center from "layouts/wrapper/Center";
import P from "components/P";
import CustomButton from "components/buttons/CustomButton";
import { toast } from "react-toastify";
import { devLog } from "utils/logger";
import appstore from 'resources/img/appstore.svg';
import galaxystore from "resources/img/galaxystore.png";
import onestore from "resources/img/onestore.png";

function DoorSuccessPage() {
    const { id } = useParams(); // storeCode
    const location = useLocation();
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(5);
    const { doorOpened, deviceId, timestamp } = location.state || {};

    const timeout = 300;
    const [deferredPrompt, setDeferredPrompt] = useState(null);

    useEffect(() => {
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        });
    }, []);

    const redirectToStore = (storeUrl) => {
        window.location.href = storeUrl;
    };

    const tryOpenApp = (schemeUrl, storeUrl) => {
        const start = Date.now();
        const ifr = document.createElement('iframe');
        ifr.style.display = 'none';
        ifr.src = schemeUrl;
        document.body.appendChild(ifr);

        setTimeout(() => {
            const end = Date.now();
            if (end - start < timeout + 200) {
                redirectToStore(storeUrl);
            }
            document.body.removeChild(ifr);
        }, timeout);
    };

    const handleAppStoreClick = () => {
        const iosUrl = 'https://apps.apple.com/kr/app/puffu/id6504068927';
        window.location.href = iosUrl;
    };

    const handleOneStoreClick = () => {
        const onestoreUrl = 'https://onesto.re/0000779461';
        redirectToStore(onestoreUrl);
    };

    const handleGalaxyStoreClick = () => {
        const galaxyStoreUrl = 'https://apps.samsung.com/appquery/appDetail.as?appId=com.puffu.puffupayment';
        redirectToStore(galaxyStoreUrl);
    };

    // 웹뷰 체크 함수
    const isOurAppWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();

        // 웹뷰 감지 로직
        const isWebView = (() => {
            const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
            const isAndroidWebView = /wv/.test(userAgent) || /android.*samsungbrowser\/(?!.*chrome)/i.test(userAgent);

            return isIOSWebView || isAndroidWebView;
        })();

        // 웹뷰 확인
        if (isWebView) {
            if (window.BRIDGE && window.BRIDGE.isOurAppWebView && window.BRIDGE.isOurAppWebView()) {
                return true;
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                return true;
            }
        }

        return false;
    };

    // 창 닫기 함수
    const closeWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();

        // 웹뷰인 경우
        if (isOurAppWebView()) {
            if (window.BRIDGE && typeof window.BRIDGE.closeWebView === "function") {
                window.BRIDGE.closeWebView();
            } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.BRIDGE) {
                window.webkit.messageHandlers.BRIDGE.postMessage('closeWebView');
            }
        } else {
            // 웹뷰가 아닌 경우
            try {
                window.close();
            } catch (error) {
                devLog('브라우저에서 창을 닫을 수 없습니다.');
                // 메인 페이지로 이동
                navigate(`/ws/${id}/mobile/main`, { replace: true });
            }
        }
    };

    // 뒤로가기 방지
    useEffect(() => {
        if (!doorOpened) {
            // 직접 URL로 접근한 경우 메인 페이지로 리다이렉트
            // navigate(`/ws/${id}/mobile/main`, { replace: true });
            return;
        }

        // 페이지 첫 로드 시 이전 페이지로의 접근 차단을 위한 초기화
        window.history.pushState(null, null, window.location.href);

        // HTML5 History API를 이용한 뒤로가기 방지 패턴
        const blockBack = (e) => {
            // 이전 페이지로 가는 것을 방지하고 현재 페이지 유지
            window.history.pushState(null, null, window.location.href);
            toast.info("출입문 처리 완료 페이지에서는 뒤로가기를 사용할 수 없습니다.", {
                autoClose: 2000, position: toast.POSITION.BOTTOM_CENTER
            });
        };

        // 브라우저 뒤로가기 버튼 클릭 감지를 위한 이벤트 리스너
        window.addEventListener('popstate', blockBack);

        // 페이지 새로고침/닫기 방지 (선택적)
        const preventRefresh = (e) => {
            e.preventDefault();
            e.returnValue = "이 페이지를 떠나시겠습니까?";
            return e.returnValue;
        };
        window.addEventListener('beforeunload', preventRefresh);

        // 하드웨어 뒤로가기 버튼 처리 (모바일)
        document.addEventListener('backbutton', (e) => {
            e.preventDefault();
            toast.info("출입문 처리 완료 페이지에서는 뒤로가기를 사용할 수 없습니다.", {
                autoClose: 2000, position: toast.POSITION.BOTTOM_CENTER
            });
        }, false);

        // 카운트다운 타이머
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);

                    // 웹뷰인 경우 자동으로 닫기
                    if (isOurAppWebView()) {
                        closeWebView();
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // 클린업 함수
        return () => {
            window.removeEventListener('popstate', blockBack);
            window.removeEventListener('beforeunload', preventRefresh);
            document.removeEventListener('backbutton', () => { }, false);
            clearInterval(timer);
        };
    }, [id, doorOpened, navigate]);

    // 수동으로 닫기 버튼 처리
    const handleCloseButton = () => {
        closeWebView();
    };


    return (
        <Container>
            <VerticalFlex gap={30} alignItems={"center"} justifyContent={"center"} minHeight={"100vh"} padding={"20px"}>
                <FlexChild>
                    <Center>
                        <div style={{
                            backgroundColor: "#4CAF50",
                            borderRadius: "50%",
                            width: "120px",
                            height: "120px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "20px"
                        }}>
                            <span style={{ fontSize: "60px", color: "white" }}>✓</span>
                        </div>
                    </Center>
                </FlexChild>

                <FlexChild>
                    <Center>
                        <P color={"black"} weight={700} size={28}>출입문이 열렸습니다!</P>
                    </Center>
                </FlexChild>
                <FlexChild>
                    <Center>
                        {
                            isOurAppWebView()
                                ?
                                <P color={"#999"} size={14}>
                                    {`${countdown}초 후 자동으로 창이 닫힙니다.`}
                                </P>
                                :
                                <VerticalFlex gap={20} alignItems={"center"}>
                                    <P color={"#666"} size={14} style={{ textAlign: 'center' }}>
                                        어플을 사용하면 더욱 편리하게 이용할 수 있어요.
                                    </P>

                                    <VerticalFlex gap={10} alignItems={"center"} style={{ marginTop: 10 }}>
                                        <img src={appstore} alt="App Store" onClick={handleAppStoreClick} style={{ cursor: 'pointer', width: '180px' }} />
                                        <img src={onestore} alt="One Store" onClick={handleOneStoreClick} style={{ cursor: 'pointer', width: '180px' }} />
                                        <img src={galaxystore} alt="Galaxy Store" onClick={handleGalaxyStoreClick} style={{ cursor: 'pointer', width: '180px' }} />
                                    </VerticalFlex>
                                </VerticalFlex>
                        }
                    </Center>
                </FlexChild>


                {/* {deviceId && timestamp && (
                    <FlexChild>
                        <div style={{ marginTop: "30px", borderTop: "1px solid #eee", paddingTop: "15px", fontSize: "12px", color: "#999" }}>
                            <p>장치 ID: {deviceId}</p>
                            <p>처리 시간: {new Date(timestamp).toLocaleString()}</p>
                        </div>
                    </FlexChild>
                )} */}
            </VerticalFlex>
        </Container>
    );
}

export default DoorSuccessPage; 